import React from "react";
import {useSelector} from "react-redux";
import {styled, theme, Collapsible, Card, H2} from "@reside/ui";
import {ButtonLink} from "../../atoms/button-link";
import {SsoConfigurationResponse} from "../../services/SsoService";
import {BackButton} from "./BackButton";
import {select} from "../../store";
import {RoleNameEnum} from "../../utils/types/common";
import {IDP_CLIENT_FIELD_LABELS, IDP_CLIENT_OPTIONS} from "./constants";

const ConfigItem = styled.div`
  margin-bottom: ${theme.space[5]}px;
`;

const ConfigLabel = styled.div`
  font-size: ${theme.fontSize[2]}px;
  font-weight: 600;
  color: ${theme.color.darkBlue100};
  margin-bottom: ${theme.space[3]}px;
`;

const ConfigValue = styled.div`
  font-size: ${theme.fontSize[3]}px;
  color: ${theme.color.gray100};
  word-break: break-all;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.space[5]}px;
`;

type ConfigItemRowProps = Readonly<{
  label: string;
  value: string;
}>;

const ConfigItemRow = ({label, value}: ConfigItemRowProps) => (
  <ConfigItem>
    <ConfigLabel>{label}</ConfigLabel>
    <ConfigValue>{value}</ConfigValue>
  </ConfigItem>
);

type SsoConfigDetailsProps = Readonly<{
  ssoConfig: SsoConfigurationResponse;
  organizationId: string;
}>;

const SsoConfigDetails = ({
  ssoConfig,
  organizationId,
}: SsoConfigDetailsProps) => {
  const fieldLabels =
    IDP_CLIENT_FIELD_LABELS[
      ssoConfig.idpClient as keyof typeof IDP_CLIENT_FIELD_LABELS
    ] ?? IDP_CLIENT_FIELD_LABELS.onelogin;
  const idpClientLabel =
    IDP_CLIENT_OPTIONS.find(o => o.value === ssoConfig.idpClient)?.label ??
    ssoConfig.idpClient;

  return (
    <Collapsible openTitle="Show details" closeTitle="Hide details">
      <CardHeader>
        <H2>SSO Configuration</H2>

        <ButtonLink
          to={`/admin/organizations/${organizationId}/sso-configuration/edit`}
        >
          Edit details
        </ButtonLink>
      </CardHeader>

      <ConfigItemRow label="IDP Client" value={idpClientLabel} />
      <ConfigItemRow label="x.509 Certificate" value="••••••••••••••••••••" />
      <ConfigItemRow
        label={fieldLabels.issuerUrl}
        value={ssoConfig.issuerUrl}
      />
      <ConfigItemRow
        label={fieldLabels.samlEndpoint}
        value={ssoConfig.samlEndpoint}
      />
      <ConfigItemRow
        label={fieldLabels.sloEndpoint}
        value={ssoConfig.sloEndpoint}
      />
    </Collapsible>
  );
};

type Props = Readonly<{
  ssoConfig: SsoConfigurationResponse;
  organizationId: string;
}>;

/**
 * Displays SSO configuration details in collapsible card.
 */
export const SsoConfigurationCard = ({ssoConfig, organizationId}: Props) => {
  const userRole = useSelector(select.adminSession.userRoleName);
  const isOrganizationItAdmin = userRole === RoleNameEnum.ORGANIZATION_IT_ADMIN;

  return (
    <>
      {!isOrganizationItAdmin && (
        <BackButton
          to={`/admin/organizations/${organizationId}/edit`}
          label="Edit Organization"
        />
      )}
      <Card style={{padding: theme.space[7]}}>
        <SsoConfigDetails
          ssoConfig={ssoConfig}
          organizationId={organizationId}
        />
      </Card>
    </>
  );
};
